import { UNSAFE_expValNoExposure } from '@atlaskit/platform-feature-experiments/unsafe-exp-val-no-exposure';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import type {
	ContentContext,
	ContentType,
	useChatContextProvider,
} from '@atlassian/conversation-assistant-store/controllers/chat-context/store';

import { getCurrentPageId } from '../bridge-api/message-stream-action/getCurrentPageId';
import { StreamLifecycleState } from '../bridge-api/streaming-lifecycle/StreamLifecycleState';

/**
 * Whether the Remix state rearchitecture's Object Sidebar treatment is on. Read without exposure:
 * Confluence records that experiment's exposure exactly once, at Object Sidebar Remix admission.
 */
const isRemixObjectSidebarLifecycleEnabled = (): boolean =>
	fg('remix_state_rearchitecture_enabled') &&
	UNSAFE_expValNoExposure('remix_state_rearchitecture_object_sidebar', 'isEnabled', false) === true;

export const createBroadcastStreamLifecycle = ({
	broadcastStreamStatus,
	chatContextStoreActions,
	contentContextValue,
	targetId,
	assistantMessageId,
	inlineRovoEditAttemptId,
}: {
	broadcastStreamStatus?: (
		status: StreamLifecycleState,
		streamId: string,
		content?: { id: string | number; type: ContentType; inlineRovoEditAttemptId?: string },
	) => void;
	chatContextStoreActions: ReturnType<typeof useChatContextProvider>;
	contentContextValue: ContentContext;
	targetId?: string | number;
	assistantMessageId: string;
	inlineRovoEditAttemptId?: string;
}) => {
	return (status: StreamLifecycleState): void => {
		let contentTarget;
		if (expValEquals('cc-maui-experiment', 'isEnabled', true)) {
			// Use targetId from sendMessage if provided, otherwise fall back to currentTargetId
			const activeTargetId = targetId ?? chatContextStoreActions.getCurrentTargetId();
			contentTarget = chatContextStoreActions.getTarget(contentContextValue, activeTargetId);
		} else {
			contentTarget = chatContextStoreActions.getTarget(contentContextValue, targetId);
		}

		// CCCEMAU-3764: stream data is routed to the editor for the current page even without a
		// registered content target (see `resolveEditorContentId`), for example a Remix started in
		// view mode on a classic page whose editor mounted mid-generation. A terminal broadcast has to
		// reach that same editor, or the `onStreamStart` side effects (disabled AI entry points) are
		// only undone by the bridge inactivity timeout. `send-context-ready` precedes any stream data,
		// so it keeps requiring a registered target. Gated with the Object Sidebar Remix lifecycle that
		// produces these ownerless streams.
		const fallbackPageId =
			!contentTarget &&
			status !== StreamLifecycleState.SEND_CONTEXT_READY &&
			isRemixObjectSidebarLifecycleEnabled()
				? getCurrentPageId()
				: undefined;

		broadcastStreamStatus?.(
			status,
			assistantMessageId,
			contentTarget
				? {
						id: contentTarget.id,
						type: contentTarget.type as ContentType,
						...(inlineRovoEditAttemptId && { inlineRovoEditAttemptId }),
					}
				: fallbackPageId
					? {
							id: fallbackPageId,
							type: 'page',
							...(inlineRovoEditAttemptId && { inlineRovoEditAttemptId }),
						}
					: undefined,
		);
	};
};
